import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import { AlertTriangle } from "lucide-react";
import { Sidebar, type SidebarItem } from "@/components/shell/Sidebar";
import { useSessionsFeed, type SessionFeedRow } from "@/data/session-feed";
import {
  spokenMemoryKeys,
  useSessionPinsQuery,
  type PinRow,
} from "@/data/spoken-memory";
import { useSession } from "@/data/auth";
import { useCurrentWorkspace } from "@/data/workspaces";
import { writeLastWorkspaceSlug } from "@/data/root-redirect";
import { useQueryClient } from "@tanstack/react-query";

const RECENT_LIMIT = 10;
const EMPTY_PINS: PinRow[] = [];

interface WorkspaceFrameRenderProps {
  highlightedSessionId: string | null;
  isCurrentSessionPinned: boolean;
  prevSessionId: string | null;
  nextSessionId: string | null;
  onOpenSession: (id: string) => void;
  onOpenHome: () => void;
  onOpenSettings: () => void;
  onPinsChanged: () => void;
}

interface WorkspaceFrameProps {
  workspaceSlug: string;
  activeView: "home" | "session" | "browse";
  sessionId?: string | null;
  children:
    | React.ReactNode
    | ((props: WorkspaceFrameRenderProps) => React.ReactNode);
}

export function WorkspaceFrame({
  workspaceSlug,
  activeView,
  sessionId = null,
  children,
}: WorkspaceFrameProps) {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const session = useSession();
  const { rows: feedRows } = useSessionsFeed(workspaceSlug);
  const pinsQuery = useSessionPinsQuery(workspaceSlug);
  const openableFeedRows = useMemo(
    () => feedRows.filter((row) => row.openable),
    [feedRows],
  );
  const [highlightedSessionId, setHighlightedSessionId] = useState<
    string | null
  >(null);
  const pins = pinsQuery.data?.pins ?? EMPTY_PINS;
  const pinnedIds = useMemo(() => buildPinnedIdSet(pins), [pins]);
  const { prevSessionId, nextSessionId } = useMemo(
    () =>
      activeView === "session"
        ? adjacentSessionIds(openableFeedRows, sessionId)
        : { prevSessionId: null, nextSessionId: null },
    [activeView, openableFeedRows, sessionId],
  );

  const onOpenHome = useCallback(() => {
    void navigate({
      to: "/$workspaceSlug",
      params: { workspaceSlug },
    });
  }, [navigate, workspaceSlug]);

  const onOpenSession = useCallback(
    (id: string) => {
      void navigate({
        to: "/$workspaceSlug/captures/$sessionId",
        params: { workspaceSlug, sessionId: id },
      });
    },
    [navigate, workspaceSlug],
  );

  const onOpenSettings = useCallback(() => {
    void navigate({
      to: "/$workspaceSlug/settings",
      params: { workspaceSlug },
      search: { tab: "workspace" },
    });
  }, [navigate, workspaceSlug]);

  const onSwitchWorkspace = useCallback(
    (nextSlug: string) => {
      writeLastWorkspaceSlug(nextSlug);
      void navigate({
        to: "/$workspaceSlug",
        params: { workspaceSlug: nextSlug },
      });
    },
    [navigate],
  );

  const onOpenOnboarding = useCallback(() => {
    void navigate({
      to: "/onboarding",
      search: { workspace: undefined, return_to: undefined },
    });
  }, [navigate]);

  const sidebarItems = useMemo(() => {
    const isHighlighted = (id: string) =>
      (activeView === "session" && sessionId === id) ||
      (activeView === "home" && highlightedSessionId === id);
    const recent: SidebarItem[] = feedRows
      .filter((row) => row.openable)
      .slice(0, RECENT_LIMIT)
      .map((row) => ({
        id: row.id,
        title: titleNode(row),
        when: relativeWhen(row.startedAt),
        highlighted: isHighlighted(row.id),
      }));
    const pinned: SidebarItem[] = feedRows
      .filter((row) => row.openable && pinnedIds.has(row.id))
      .map((row) => ({
        id: row.id,
        title: `★ ${row.title}`,
        when: relativeWhen(row.startedAt),
        highlighted: isHighlighted(row.id),
      }));
    return { recent, pinned };
  }, [activeView, feedRows, highlightedSessionId, pinnedIds, sessionId]);

  useEffect(() => {
    if (openableFeedRows.length === 0) {
      if (highlightedSessionId !== null) setHighlightedSessionId(null);
      return;
    }

    if (activeView === "session" && sessionId) {
      if (
        sessionId !== highlightedSessionId &&
        openableFeedRows.some((row) => row.id === sessionId)
      ) {
        setHighlightedSessionId(sessionId);
      }
      return;
    }

    if (
      activeView === "home" &&
      (!highlightedSessionId ||
        !openableFeedRows.some((row) => row.id === highlightedSessionId))
    ) {
      setHighlightedSessionId(openableFeedRows[0]?.id ?? null);
    }
  }, [activeView, highlightedSessionId, openableFeedRows, sessionId]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || hasKeyboardModifier(event)) return;
      if (isEditableKeyboardTarget(event.target)) return;

      if (event.key === "Escape" && activeView === "session") {
        event.preventDefault();
        onOpenHome();
        return;
      }

      if (
        (event.key === "j" || event.key === "k") &&
        (activeView === "home" || activeView === "session")
      ) {
        const currentId =
          activeView === "session" ? sessionId : highlightedSessionId;
        const nextId = getAdjacentSessionId(
          openableFeedRows,
          currentId,
          event.key === "j" ? 1 : -1,
        );
        if (!nextId) return;

        event.preventDefault();
        setHighlightedSessionId(nextId);
        if (activeView === "session") onOpenSession(nextId);
        return;
      }

      if (event.key === "Enter" && activeView === "home") {
        if (shouldLeaveEnterForFocusedControl(event.target)) return;

        const selectedId = highlightedSessionId ?? openableFeedRows[0]?.id;
        if (!selectedId) return;

        event.preventDefault();
        onOpenSession(selectedId);
      }
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [
    activeView,
    highlightedSessionId,
    onOpenHome,
    onOpenSession,
    openableFeedRows,
    sessionId,
  ]);

  const renderProps: WorkspaceFrameRenderProps = {
    highlightedSessionId,
    isCurrentSessionPinned: sessionId ? pinnedIds.has(sessionId) : false,
    prevSessionId,
    nextSessionId,
    onOpenSession,
    onOpenHome,
    onOpenSettings,
    onPinsChanged: () => {
      void queryClient.invalidateQueries({
        queryKey: spokenMemoryKeys.pins(workspaceSlug),
      });
    },
  };

  return (
    <>
      <LastWorkspaceTracker workspaceSlug={workspaceSlug} />
      <Sidebar
        homeActive={activeView === "home"}
        pinned={sidebarItems.pinned}
        recent={sidebarItems.recent}
        onOpenHome={onOpenHome}
        onOpenSession={onOpenSession}
        onOpenSettings={onOpenSettings}
        onSwitchWorkspace={onSwitchWorkspace}
        onOpenOnboarding={onOpenOnboarding}
        onLogout={session.logout}
      />

      <main className="min-w-0 flex-1 overflow-auto">
        {typeof children === "function" ? children(renderProps) : children}
      </main>
    </>
  );
}

function LastWorkspaceTracker({ workspaceSlug }: { workspaceSlug: string }) {
  const { loading, currentMembership } = useCurrentWorkspace(workspaceSlug);

  useEffect(() => {
    if (loading || !currentMembership) return;
    writeLastWorkspaceSlug(currentMembership.workspace.slug);
  }, [currentMembership, loading]);

  return null;
}

function buildPinnedIdSet(pins: PinRow[]): Set<string> {
  const set = new Set<string>();
  for (const pin of pins) set.add(String(pin.session_id));
  return set;
}

function titleNode(row: SessionFeedRow): React.ReactNode {
  if (row.iconKind === "processing") {
    return (
      <span className="flex items-center gap-2 text-(--color-pill-ambient)">
        <span className="proto-live-spinner" />
        {row.title}
      </span>
    );
  }
  if (row.iconKind === "stale") {
    return (
      <span className="flex items-center gap-2 text-(--color-pill-attention)">
        <AlertTriangle className="h-3 w-3 shrink-0" />
        {row.title}
      </span>
    );
  }
  return row.title;
}

function hasKeyboardModifier(event: KeyboardEvent): boolean {
  return event.altKey || event.ctrlKey || event.metaKey;
}

function isEditableKeyboardTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  const tagName = target.tagName.toLowerCase();
  return (
    target.isContentEditable ||
    tagName === "input" ||
    tagName === "textarea" ||
    tagName === "select"
  );
}

function shouldLeaveEnterForFocusedControl(
  target: EventTarget | null,
): boolean {
  if (isEditableKeyboardTarget(target)) return true;
  if (!(target instanceof HTMLElement)) return false;

  const focusedControl = target.closest(
    "button,a,[role='button'],[role='menuitem']",
  );
  return Boolean(
    focusedControl && !target.closest("[data-session-list-row='true']"),
  );
}

function getAdjacentSessionId(
  rows: SessionFeedRow[],
  currentId: string | null,
  direction: 1 | -1,
): string | null {
  if (rows.length === 0) return null;

  const currentIndex = currentId
    ? rows.findIndex((row) => row.id === currentId)
    : -1;
  const baseIndex =
    currentIndex >= 0 ? currentIndex : direction > 0 ? -1 : rows.length;
  const nextIndex = Math.min(
    rows.length - 1,
    Math.max(0, baseIndex + direction),
  );
  return rows[nextIndex]?.id ?? null;
}

function adjacentSessionIds(
  rows: SessionFeedRow[],
  currentId: string | null,
): { prevSessionId: string | null; nextSessionId: string | null } {
  const index = currentId ? rows.findIndex((row) => row.id === currentId) : -1;
  if (index < 0) return { prevSessionId: null, nextSessionId: null };
  return {
    prevSessionId: index > 0 ? (rows[index - 1]?.id ?? null) : null,
    nextSessionId:
      index < rows.length - 1 ? (rows[index + 1]?.id ?? null) : null,
  };
}

function relativeWhen(ms: number): string {
  const diff = Date.now() - ms;
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 60) return minutes < 1 ? "now" : `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  return `${Math.floor(days / 7)}w`;
}
